import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Clock, CheckCircle, RotateCcw } from 'lucide-react';
import PageLoader from '../components/PageLoader';
import LoadMoreButton from '../components/LoadMoreButton';
import { authConfig } from '../config/auth';
import { useAuth } from '../contexts/AuthContext';
import { usePaginatedFetch } from '../hooks/usePaginatedFetch';
import type { ClaimedReward } from '@crewcast/shared';
import '../styles/rewards.css';

export default function ClaimedRewardsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const {
    items: claimedRewards,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
  } = usePaginatedFetch<ClaimedReward>({
    url: `${authConfig.apiUrl}/api/claimed-rewards`,
    token,
    dataKey: 'claimedRewards',
    limit: 20,
  });

  if (loading) {
    return (
      <div className="rewards-container">
        <PageLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rewards-container">
        <div className="rewards-error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="rewards-container">
      <div className="rewards-header">
        <div>
          <h1 className="rewards-title">My Claimed Rewards</h1>
          <p className="rewards-subtitle">
            {claimedRewards.length}{' '}
            {claimedRewards.length === 1 ? 'claim' : 'claims'}
          </p>
        </div>

        <button className="rewards-secondary-btn" onClick={() => navigate('/rewards')}>
          Back to Rewards
        </button>
      </div>

      {claimedRewards.length === 0 ? (
        <div className="rewards-empty">
          <div className="rewards-empty-icon">
            <Gift size={48} color="#00bf62" />
          </div>
          <h2>No claims yet</h2>
          <p>Claim a reward to see it show up here.</p>
        </div>
      ) : (
        <div className="rewards-grid">
          {claimedRewards.map((claim) => (
            <div key={claim.claimId} className="rewards-card">
              {claim.image && (
                <div className="rewards-card-image">
                  <img src={claim.image} alt={claim.title} />
                </div>
              )}
              <div className="rewards-card-content">
                <h3 className="rewards-card-title">{claim.title}</h3>
                {claim.description && (
                  <p className="rewards-card-description">{claim.description}</p>
                )}
                <div className="rewards-card-footer">
                  <span className="rewards-card-points">
                    {claim.pointsToClaim} pts
                  </span>
                  <span
                    className={`rewards-status-badge ${
                      claim.status === 'claimed'
                        ? 'pending'
                        : claim.status === 'fulfilled'
                        ? 'approved'
                        : 'rejected'
                    }`}
                  >
                    {claim.status === 'claimed'
                      ? <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Clock size={16} /> Claimed</span>
                      : claim.status === 'fulfilled'
                      ? <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><CheckCircle size={16} /> Fulfilled</span>
                      : <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><RotateCcw size={16} /> Cancelled</span>}
                  </span>
                </div>
                <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--color-gray-500)' }}>
                  Claimed {new Date(claim.claimedAt).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <LoadMoreButton onClick={loadMore} loading={loadingMore} hasMore={hasMore} />
    </div>
  );
}


